import React, { useState, useEffect, useCallback } from 'react';
import { Input, Tree, Spin, Typography, Empty, Flex, Button, Select } from 'antd';
import {
  ReloadOutlined,
  FolderOutlined,
  FileOutlined,
  FileUnknownOutlined,
  QuestionOutlined,
  DownOutlined,
  NodeExpandOutlined,
  NodeCollapseOutlined,
} from '@ant-design/icons';
import { usePipelineActions, useMarkdownActions, useSummaryActions } from '../store/store';
import useTabManagerStore from '../store/tabManagerStore';
import ResizablePanel from './ResizablePanel';
import PipelineIcon from '../assets/icons/pipeline.svg?react';
import WorkflowIcon from '../assets/icons/workflow.svg?react';
import { createContextLogger } from '../utils/logger';
import { filesService, FileNode, apiClient } from '../api';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';
import { useTelemetry } from '../hooks/useTelemetry';

const logger = createContextLogger('FileBrowser');

const { Search } = Input;
const { Title } = Typography;

const FILE_TYPE_OPTIONS = [
  { label: 'Pipeline', value: 'PIPELINE' },
  { label: 'Workflow', value: 'WORKFLOW' },
  { label: 'Markdown', value: 'MARKDOWN' },
  { label: 'File', value: 'FILE' },
];

interface TreeNodeData {
  key: string;
  title: string;
  isLeaf: boolean;
  isFolder: boolean;
  hopFileType?: string;
  selectable: boolean;
  disabled: boolean;
  icon: React.ReactElement;
  children?: TreeNodeData[];
}


// Define icons for different file types
const getFileIcon = (hopFileType: string): React.ReactElement => {
  switch (hopFileType) {
    case 'FOLDER':
      return <FolderOutlined style={{ color: '#f8c14a' }} />;
    case 'WORKFLOW':
      return <WorkflowIcon />;
    case 'PIPELINE':
      return <PipelineIcon />;
    case 'FILE':
      return <FileOutlined style={{ color: '#8c8c8c' }} />;
    case 'MARKDOWN':
      return <FileOutlined style={{ color: '#8c8c8c' }} />;
    case 'UNRECOGNIZED':
      return <FileUnknownOutlined style={{ color: '#ff4d4f' }} />;
    default:
      return <QuestionOutlined style={{ color: '#d9d9d9' }} />;
  }
};

// Transform file data to Ant Design Tree format
const transformToAntdTreeData = (nodes: FileNode[]): TreeNodeData[] => {
  if (!nodes) return [];
  return nodes
    .map((node: FileNode): TreeNodeData => {
      const antNode: TreeNodeData = {
        key: node.path + node.name,
        title: node.name,
        isLeaf: !node.isFolder,
        isFolder: node.isFolder,
        hopFileType: node.hopFileType,
        selectable: true,
        disabled: false,
        icon: getFileIcon(node.hopFileType || 'FILE'),
      };

      if (node.children && node.children.length > 0) {
        antNode.children = transformToAntdTreeData(node.children);
        // Sort children by directory status first, then by key
        antNode.children.sort((a: TreeNodeData, b: TreeNodeData) => {
          // If one is a directory and the other is not, prioritize the directory
          if (a.isFolder !== b.isFolder) {
            return b.isFolder ? 1 : -1;
          }
          // Otherwise sort by key
          return a.key.localeCompare(b.key);
        });
      }

      return antNode;
    })
    .sort((a: TreeNodeData, b: TreeNodeData) => {
      // If one is a directory and the other is not, prioritize the directory
      if (a.isFolder !== b.isFolder) {
        return b.isFolder ? 1 : -1;
      }
      // Otherwise sort by key
      return a.key.localeCompare(b.key);
    }); // Sort the nodes at this level
};

// Fuse.js configuration for fuzzy search
const fuseOptions = {
  keys: [
    'title', 
    'key',
    // Add normalized versions for better matching
    {
      name: 'normalizedTitle',
      getFn: (item: TreeNodeData) => item.title.replace(/[_-]/g, ' ')
    },
    {
      name: 'compactTitle', 
      getFn: (item: TreeNodeData) => item.title.replace(/[_\s-]/g, '')
    }
  ],
  threshold: 0.4, // Lower = more strict, higher = more fuzzy (0.0 = exact, 1.0 = match anything)
  distance: 100, // Maximum distance to search
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 1,
  ignoreLocation: true, // Don't care where in the string the match is
  findAllMatches: true
};

// Flatten tree structure for Fuse.js search
const flattenTreeData = (nodes: TreeNodeData[]): TreeNodeData[] => {
  const flattened: TreeNodeData[] = [];
  
  const traverse = (nodeList: TreeNodeData[]) => {
    nodeList.forEach(node => {
      // Only add files to search results, not folders (unless you want to search folders too)
      if (!node.isFolder) {
        flattened.push(node);
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };
  
  traverse(nodes);
  return flattened;
};

// Rebuild tree structure from search results
const rebuildTreeFromResults = (searchResults: any[], originalTree: TreeNodeData[]): TreeNodeData[] => {
  if (searchResults.length === 0) return [];
  
  const resultKeys = new Set(searchResults.map(result => result.item.key));
  const pathsToInclude = new Set<string>();
  
  // Add all parent paths for each result
  searchResults.forEach(result => {
    const path = result.item.key;
    const pathParts = path.split('/');
    for (let i = 1; i <= pathParts.length; i++) {
      pathsToInclude.add(pathParts.slice(0, i).join('/'));
    }
  });
  
  const filterNode = (node: TreeNodeData): TreeNodeData | null => {
    const shouldIncludeNode = pathsToInclude.has(node.key) || resultKeys.has(node.key);
    
    if (node.children) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((child): child is TreeNodeData => child !== null);
      
      if (filteredChildren.length > 0 || shouldIncludeNode) {
        return { ...node, children: filteredChildren };
      }
    } else if (shouldIncludeNode) {
      return { ...node };
    }
    
    return null;
  };
  
  return originalTree
    .map(filterNode)
    .filter((node): node is TreeNodeData => node !== null);
};

// Enhanced filter function using Fuse.js
const filterTreeData = (nodes: TreeNodeData[], term: string): TreeNodeData[] => {
  if (!term || term.trim().length === 0) return nodes;
  
  // Flatten the tree for searching
  const flatItems = flattenTreeData(nodes);
  
  // Create Fuse instance
  const fuse = new Fuse(flatItems, fuseOptions);
  
  // Perform search
  const searchResults = fuse.search(term.trim());
  
  // Log search info for debugging
  logger.debug('Fuse.js search results:', {
    query: term,
    totalItems: flatItems.length,
    results: searchResults.length,
    scores: searchResults.slice(0, 5).map(r => ({ title: r.item.title, score: r.score }))
  });
  
  // Rebuild tree structure with results
  return rebuildTreeFromResults(searchResults, nodes);
};

const FileBrowser: React.FC = () => {
  const { t } = useTranslation();
  const { trackEvent, trackUserInteraction } = useTelemetry('FileBrowser');
  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>(() => {
    const stored = localStorage.getItem('fileBrowser_expandedKeys');
    return stored ? JSON.parse(stored) : [];
  });
  const [filteredTreeData, setFilteredTreeData] = useState<TreeNodeData[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);

  // Get tab manager actions
  const { openTab, updateTabModified, tabs } = useTabManagerStore((state) => ({
    openTab: state.openTab,
    updateTabModified: state.updateTabModified,
    tabs: state.tabs,
  }));

  // Get pipeline actions
  const { setActivePipeline, createPipeline } = usePipelineActions();

  // Get Markdown actions
  const { addMarkdown } = useMarkdownActions();

  // Get Summary actions
  const { addSummary } = useSummaryActions();

  // Save expanded keys to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('fileBrowser_expandedKeys', JSON.stringify(expandedKeys));
  }, [expandedKeys]);

  // Listen for folder expansion events from global search
  useEffect(() => {
    const handleExpandFolder = (event: CustomEvent) => {
      const { folderPath } = event.detail;
      logger.debug('Expanding folder from global search:', { 
        folderPath, 
        currentExpandedKeys: expandedKeys
      });
      
      // Add the folder path to expanded keys if it's not already expanded
      if (!expandedKeys.includes(folderPath)) {
        setExpandedKeys(prev => [...prev, folderPath]);
        logger.debug('Added folder to expanded keys:', folderPath);
      } else {
        logger.debug('Folder already expanded:', folderPath);
      }
    };

    window.addEventListener('expandFolder', handleExpandFolder as EventListener);
    
    return () => {
      window.removeEventListener('expandFolder', handleExpandFolder as EventListener);
    };
  }, [expandedKeys]);

  // Listen for refresh events (e.g., after saveAs)
  useEffect(() => {
    const handleRefreshEvent = () => {
      logger.debug('Refresh event received - refreshing file browser');
      handleRefresh();
    };

    window.addEventListener('refreshFileBrowser', handleRefreshEvent);
    
    return () => {
      window.removeEventListener('refreshFileBrowser', handleRefreshEvent);
    };
  }, [treeData.length, searchTerm, expandedKeys.length]);

  // Fetch file structure using API service
  const fetchFileStructure = useCallback(() => {
    setLoading(true);
    setError(null);
    
    filesService.getFileList()
      .then((data: FileNode) => {
        const antdData = transformToAntdTreeData([data]);
        setTreeData(antdData);
        setFilteredTreeData(antdData);
        
        // Auto-expand root folder if no expanded keys are stored (first time use)
        const storedKeys = localStorage.getItem('fileBrowser_expandedKeys');
        if (!storedKeys || JSON.parse(storedKeys).length === 0) {
          if (antdData.length > 0 && antdData[0].isFolder) {
            const rootKey = antdData[0].key;
            setExpandedKeys([rootKey]);
            logger.debug('Auto-expanded root folder:', rootKey);
          }
        }
      })
      .catch((err: any) => {
        const errorMessage = err instanceof Error ? err.message : t('fileBrowser.failedToLoad');
        logger.error('Fetch error:', err);
        setError(errorMessage);
        setTreeData([]);
        setFilteredTreeData([]);
      })
      .finally(() => setLoading(false));
  }, [t]);

  // Initial fetch
  useEffect(() => {
    fetchFileStructure();
  }, [fetchFileStructure]);

  // Filter tree data based on file type
  const filterByFileType = useCallback(
    (nodes: TreeNodeData[]): TreeNodeData[] => {
      // Always include FOLDER in the filter
      const effectiveTypes =
        selectedFileTypes && selectedFileTypes.length > 0 ? [...selectedFileTypes, 'FOLDER'] : null;
      if (!effectiveTypes) return nodes;
      const filterNode = (node: TreeNodeData): TreeNodeData | null => {
        if (node.hopFileType && !effectiveTypes.includes(node.hopFileType)) return null;
        if (node.children) {
          const filteredChildren = node.children.map(filterNode).filter((child: TreeNodeData | null) => child !== null) as TreeNodeData[];
          if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren };
          }
        }
        if (!node.children && node.hopFileType && effectiveTypes.includes(node.hopFileType)) {
          return { ...node };
        }
        if (!node.children && !node.hopFileType) {
          return { ...node };
        }
        return null;
      };
      return nodes.map(filterNode).filter((node: TreeNodeData | null) => node !== null) as TreeNodeData[];
    },
    [selectedFileTypes]
  );

  // Helper function to collect all parent folder keys for a given set of nodes
  const collectFolderKeys = useCallback((nodes: TreeNodeData[]): React.Key[] => {
    const folderKeys: React.Key[] = [];
    
    const traverse = (nodeList: TreeNodeData[]) => {
      nodeList.forEach(node => {
        if (node.isFolder) {
          folderKeys.push(node.key);
        }
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    
    traverse(nodes);
    return folderKeys;
  }, []);

  // Helper function to get all folder keys from the entire tree
  const getAllFolderKeys = useCallback((nodes: TreeNodeData[]): React.Key[] => {
    return collectFolderKeys(nodes);
  }, [collectFolderKeys]);

  // Helper function to get root folder key
  const getRootFolderKey = useCallback((nodes: TreeNodeData[]): React.Key | null => {
    if (nodes.length > 0 && nodes[0].isFolder) {
      return nodes[0].key;
    }
    return null;
  }, []);

  // Handle expand all functionality
  const handleExpandAll = useCallback(() => {
    const allFolderKeys = getAllFolderKeys(treeData);
    trackUserInteraction('click', 'expand-all-folders', {
      folderCount: allFolderKeys.length,
      totalNodes: treeData.length
    });
    setExpandedKeys(allFolderKeys);
    logger.debug('Expanded all folders:', { allFolderKeys });
  }, [treeData, getAllFolderKeys, trackUserInteraction]);

  // Handle collapse all functionality (keep root open)
  const handleCollapseAll = useCallback(() => {
    const rootKey = getRootFolderKey(treeData);
    const keysToKeep = rootKey ? [rootKey] : [];
    trackUserInteraction('click', 'collapse-all-folders', {
      previousExpandedCount: expandedKeys.length,
      keepRootOpen: !!rootKey
    });
    setExpandedKeys(keysToKeep);
    logger.debug('Collapsed all folders except root:', { keysToKeep });
  }, [treeData, getRootFolderKey, expandedKeys.length, trackUserInteraction]);

  // Combine file type filtering and search
  useEffect(() => {
    let filtered = filterByFileType(treeData);
    if (searchTerm) {
      filtered = filterTreeData(filtered, searchTerm);
      
      // Auto-expand folders containing search results
      if (filtered.length > 0) {
        const foldersToExpand = collectFolderKeys(filtered);
        setExpandedKeys(prev => {
          const newKeys = [...new Set([...prev, ...foldersToExpand])];
          logger.debug('Auto-expanding folders for search results:', { 
            searchTerm, 
            foldersToExpand,
            newExpandedKeys: newKeys 
          });
          return newKeys;
        });
      }
    }
    setFilteredTreeData(filtered);
  }, [treeData, searchTerm, filterByFileType, collectFolderKeys]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Helper to hide welcome overlay in TabManager
  const hideWelcomeOverlay = () => {
    window.dispatchEvent(new CustomEvent('hideWelcomeOverlay'));
  };


  // Handle selection and expansion together
  const handleSelect = async (selectedKeys: React.Key[], info: any, event?: React.MouseEvent) => {
    logger.debug('Selected file:', info.node, 'Event:', event);

    // If it's a folder, toggle expansion
    if (info.node.isFolder) {
      const key = info.node.key;
      const isExpanded = expandedKeys.includes(key);

      if (isExpanded) {
        // Collapse the node
        trackUserInteraction('click', 'folder-collapse', {
          folderPath: key,
          folderName: info.node.title
        });
        setExpandedKeys(expandedKeys.filter((k: React.Key) => k !== key));
      } else {
        // Expand the node
        trackUserInteraction('click', 'folder-expand', {
          folderPath: key,
          folderName: info.node.title
        });
        setExpandedKeys([...expandedKeys, key]);
      }
    } else {
      // Check if SHIFT key is pressed for summary view
      const isShiftClick = event?.shiftKey || false;
      logger.debug('Shift click detected:', isShiftClick);
      
      // Handle file selection
      if (
        info.node.hopFileType === 'PIPELINE' ||
        info.node.hopFileType === 'WORKFLOW' ||
        info.node.hopFileType === 'MARKDOWN'
      ) {
        // If SHIFT+Click on pipeline or workflow, show summary as tab
        if (isShiftClick && (info.node.hopFileType === 'PIPELINE' || info.node.hopFileType === 'WORKFLOW')) {
          logger.debug('SHIFT+Click detected, opening summary tab for:', info.node.title);
          
          try {
            trackUserInteraction('click', 'file-summary', {
              fileName: info.node.title,
              fileType: info.node.hopFileType,
              filePath: info.node.key
            });
            
            // Fetch file data for summary
            let fileData: any;
            if (info.node.hopFileType === 'PIPELINE') {
              fileData = await filesService.getPipelineFile(info.node.key);
            } else if (info.node.hopFileType === 'WORKFLOW') {
              fileData = await filesService.getWorkflowFile(info.node.key);
            }

            // Use default metadata since the API endpoint doesn't exist
            const metadata = {
              size: 0,
              lastModified: new Date().toISOString(),
              type: info.node.hopFileType
            };
            
            logger.debug('File data fetched:', fileData);
            logger.debug('Metadata fetched:', metadata);
            
            // Create summary data with unique ID
            const summaryTabId = `${info.node.key}-summary`;
            const summaryData = {
              id: summaryTabId,
              name: fileData.name,
              description: fileData.description,
              filename: info.node.key,
              fileType: info.node.hopFileType,
              transforms: fileData.transforms || [],
              hops: fileData.hops || [],
              notes: fileData.notes || [],
              metadata: metadata
            };

            // Add summary to store and open as tab
            addSummary(summaryData);
            openTab(summaryTabId, 'summary', `${fileData.name} - Summary`, undefined, info.node.key, false);
            hideWelcomeOverlay();

            try {
              trackEvent('file-summary-opened', {
                fileName: info.node.title,
                fileType: info.node.hopFileType,
                tabId: summaryTabId,
                dataSize: fileData ? JSON.stringify(fileData).length : 0
              });
            } catch (error) {
              // Ignore telemetry errors
            }

            return; // Don't proceed with normal file opening
          } catch (error) {
            logger.error('Error loading file summary:', error);
            try {
              trackEvent('file-summary-failed', {
                fileName: info.node.title,
                fileType: info.node.hopFileType,
                error: error instanceof Error ? error.message : String(error)
              });
            } catch (telemetryError) {
              // Ignore telemetry errors
            }
            setError(t('fileBrowser.failedToLoadFileSummary'));
            return;
          }
        }
        try {
          trackUserInteraction('click', 'file-open', {
            fileName: info.node.title,
            fileType: info.node.hopFileType,
            filePath: info.node.key
          });
          
          // Use the node key as the tabId
          const tabId = info.node.key;

          // Check if tab already exists
          const existingTab = tabs.find(tab => tab.id === tabId);
          if (existingTab) {
            // Tab already exists, just switch to it without making a backend call
            logger.debug('Tab already exists, switching to it:', tabId);
            openTab(tabId, existingTab.type, existingTab.name, undefined, existingTab.filePath, existingTab.isNew);
            setActivePipeline(tabId);
            hideWelcomeOverlay();
            
            trackEvent('file-opened-existing-tab', {
              fileName: info.node.title,
              fileType: info.node.hopFileType,
              tabId
            });
            return;
          }

          // Tab doesn't exist, make backend call to fetch fresh data
          logger.debug('Tab does not exist, fetching from backend:', tabId);
          
          let fileData: any;
          // Use the appropriate API service method based on file type
          if (info.node.hopFileType === 'PIPELINE') {
            fileData = await filesService.getPipelineFile(tabId);
          } else if (info.node.hopFileType === 'WORKFLOW') {
            fileData = await filesService.getWorkflowFile(tabId);
          } else if (info.node.hopFileType === 'MARKDOWN') {
            fileData = await filesService.getFile(tabId);
          }

          logger.debug('Fetched file data:', fileData);

          if (info.node.hopFileType === 'MARKDOWN') {
            // Open markdown tab with content
            addMarkdown(tabId, fileData.name, fileData.content);
            openTab(tabId, 'markdown', fileData.name, undefined, info.node.key, false);
            // Mark existing files as NOT modified since they're being opened from storage
            updateTabModified(tabId, false);
            hideWelcomeOverlay();
          } else {
            // Create pipeline/workflow with the fetched data
            const fileType = info.node.hopFileType === 'WORKFLOW' ? 'workflow' : 'pipeline';
            createPipeline(tabId, {
              name: fileData.name,
              description: fileData.description,
              transforms: fileData.transforms.map((transform: any) => ({
                id: transform.name,
                type: transform.nodeType,
                position: { x: transform.xloc, y: transform.yloc },
                data: {
                  name: transform.name,
                  description: transform.description,
                  type: transform.type,
                  nodeType: transform.nodeType,
                  parallel: transform.parallel,
                },
              })),
              hops: fileData.hops.map((hop: any) => ({
                from: hop.from,
                to: hop.to,
                enabled: hop.enabled,
                hopType: hop.hopType,
              })),
              notes: fileData.notes || [],
            }, fileType);

            // Open the tab and set as active
            openTab(tabId, info.node.hopFileType.toLowerCase(), info.node.title, undefined, info.node.key, false);
            // Mark existing files as NOT modified since they're being opened from storage
            updateTabModified(tabId, false);
            setActivePipeline(tabId);
            hideWelcomeOverlay();
          }

          trackEvent('file-opened-success', {
            fileName: info.node.title,
            fileType: info.node.hopFileType,
            tabId,
            dataSize: fileData ? JSON.stringify(fileData).length : 0
          });

          logger.debug('Opening file:', {
            name: info.node.title,
            type: info.node.hopFileType,
            tabId,
          });
        } catch (error) {
          logger.error('Error loading file:', error);
          trackEvent('file-open-failed', {
            fileName: info.node.title,
            fileType: info.node.hopFileType,
            error: error instanceof Error ? error.message : String(error)
          });
          setError(t('fileBrowser.failedToLoadFile'));
        }
      }
    }
  };

  // Handle explicit expand/collapse
  const handleExpand = (expandedKeys: React.Key[]) => {
    setExpandedKeys(expandedKeys);
  };

  // Handle refresh
  const handleRefresh = () => {
    trackUserInteraction('click', 'refresh-file-browser', {
      currentFileCount: treeData.length,
      searchActive: !!searchTerm,
      expandedCount: expandedKeys.length
    });
    logger.debug('🔄 Refresh button clicked - clearing cache and refreshing file structure');
    
    // Clear the file list cache before fetching to ensure fresh data from backend
    apiClient.clearFileListCache();
    
    fetchFileStructure();
  };

  return (
    <ResizablePanel>
      <Flex vertical style={{ width: '100%', height: '100%' }}>
        <Flex
          vertical
          gap="small"
          style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}
        >
          <Flex justify="space-between" align="center">
            <Title level={4} style={{ margin: 0 }}>
              {t('fileBrowser.title')}
            </Title>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={handleRefresh}
              loading={loading}
              title={t('fileBrowser.refresh')}
              data-testid="refresh-button"
            />
          </Flex>
          {/* Creative Filter Bar */}
          <Flex gap="small" align="center" style={{ margin: '8px 0' }}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 180 }}
              placeholder={t('fileBrowser.filterByFileType')}
              value={selectedFileTypes}
              onChange={setSelectedFileTypes}
              options={FILE_TYPE_OPTIONS}
            />
          </Flex>
          <Search
            placeholder={t('fileBrowser.searchFiles')}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: '100%' }}
            allowClear
          />
          {/* Expand/Collapse Controls */}
          <Flex gap="small" align="center">
            <Button
              icon={<NodeExpandOutlined />}
              size="small"
              onClick={handleExpandAll}
              title={t('fileBrowser.expandAll')}
              data-testid="expand-all-button"
            />
            <Button
              icon={<NodeCollapseOutlined />}
              size="small"
              onClick={handleCollapseAll}
              title={t('fileBrowser.collapseAll')}
              data-testid="collapse-all-button"
            />
          </Flex>
        </Flex>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          {loading && (
            <Flex justify="center" align="center" style={{ height: '100%' }}>
              <Spin />
            </Flex>
          )}
          {error && <Empty description={error} style={{ marginTop: '20px' }} />}

          {!loading && !error && filteredTreeData.length > 0 && (
            <Tree
              treeData={filteredTreeData}
              selectable
              blockNode
              expandedKeys={expandedKeys}
              onExpand={handleExpand}
              onSelect={handleSelect}
              showIcon
              showLine={true}
              switcherIcon={<DownOutlined />}
              titleRender={(nodeData) => (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect([nodeData.key], { node: nodeData }, e);
                  }}
                >
                  {nodeData.title}
                </span>
              )}
            />
          )}

          {!loading && !error && filteredTreeData.length === 0 && (
            <Empty
              description={searchTerm ? t('fileBrowser.noFilesFound') : t('fileBrowser.noFiles')}
              style={{ marginTop: '20px' }}
            />
          )}
        </div>
      </Flex>

    </ResizablePanel>
  );
};

export default FileBrowser;
